<template>
  <div class="container">
    <LayoutColumns>
      <template v-slot:main>
        <transition name="skeleton" mode="out-in">
          <ContentLoader
            v-if="status === 'pending'"
            key="loader"
            :height="170"
            :width="400"
            :primary-color="indexStore.skeleton.primaryColor"
            :secondary-color="indexStore.skeleton.secondaryColor"
          >
            <template>
              <circle cx="35" cy="35" r="35" />
              <rect x="0" y="80" rx="3" ry="3" width="220" height="26" />
              <rect x="0" y="114" rx="3" ry="3" width="300" height="12" />
              <rect x="0" y="130" rx="3" ry="3" width="380" height="8" />
              <rect x="0" y="142" rx="3" ry="3" width="280" height="8" />
            </template>
          </ContentLoader>
          <div v-else-if="person" key="content">
            <div
              v-if="personImage"
              class="rounded-full shrink-0 w-24 h-24 mr-5 mb-1 overflow-hidden bg-cover bg-center"
              :style="{
                'background-image':
                  'url(' +
                  config.public.site.legacydomain +
                  '/images/writers/' +
                  appendToFilename(personImage, '-2-96') +
                  ')',
              }"
            />
            <FallbackImage
              v-else
              class="rounded-full shrink-0 w-24 h-24 mr-5 mb-1 overflow-hidden bg-cover bg-center"
            />
            <h1
              class="headline-s !font-medium"
              :class="{ 'text-red': !isMm() }"
            >
              {{ person.Name }}
            </h1>
            <div
              v-if="person.Inactive === 0"
              class="list-title-xxs !font-medium mb-2"
              :class="{ 'font-sans': isMm() }"
            >
              {{ person.Title }}
            </div>
            <div
              v-else
              class="list-title-xxs !font-medium mb-2"
              :class="{ 'font-sans': isMm() }"
            >
              {{ $t('FormerEmployee') }}
            </div>
            <div
              v-if="person.Inactive === 0"
              class="body-s text-gray-500"
              v-html="person.Description"
            />
            <div v-if="person.Fotograf" class="body-s text-gray-500 mt-3">
              {{ $t('Photographer') }}: {{ person.Fotograf }}
            </div>
            <CommonDivider class="mt-5 mb-2" />
            <span class="inline-flex items-center">
              <template v-if="person.Inactive === 0 && person.Phone">
                <img
                  src="~assets/icons/phone.svg?url"
                  class="inline-block mr-1 w-4 h-4 opacity-35"
                />
                <span class="inline-block list-label opacity-50 mr-3">{{
                  person.Phone
                }}</span>
              </template>
              <template v-if="person.Inactive === 0 && person.Mobile">
                <img
                  src="~assets/icons/phone.svg?url"
                  class="inline-block mr-1 w-4 h-4 opacity-35"
                />
                <span class="inline-block list-label opacity-50 mr-3">{{
                  person.Mobile
                }}</span>
              </template>
              <a
                v-if="person.Email"
                class="flex items-center"
                :href="'mailto:' + person.Email"
              >
                <img
                  src="~/assets/icons/mail.svg?url"
                  class="inline-block mr-1 w-4 h-4 opacity-25"
                />
                <span
                  class="inline-block list-label opacity-50 mr-2 truncate max-w-10"
                  >{{ person.Email }}</span
                >
              </a>
              <a
                v-if="person.Inactive === 0 && person.TwitterName"
                class="rounded-full bg-blue w-[30px] h-[30px] flex shrink-0 items-center justify-center mr-2"
                :href="'https://twitter.com/' + person.TwitterName"
                target="_blank"
              >
                <component
                  :is="twitterIcon"
                  class="fill-current text-white w-4 h-4"
                />
              </a>
              <a
                v-if="person.Inactive === 0 && person.Facebook"
                class="rounded-full bg-blue w-[30px] h-[30px] flex shrink-0 items-center justify-center"
                :href="'https://facebook.com/' + person.Facebook"
                target="_blank"
              >
                <component
                  :is="facebookIcon"
                  class="fill-current text-white w-4 h-4"
                />
              </a>
            </span>
            <CommonDivider class="mt-2" />
            <CommonSectionLabel
              v-if="articles"
              class="text-left -mb-4 md:-mb-6 opacity-35"
              :title="$t('ArticleWritenBy') + ' ' + person.Name"
              :black="true"
            />
          </div>
        </transition>
      </template>
    </LayoutColumns>
  </div>
</template>

<script setup lang="ts">
import { ContentLoader } from 'vue-content-loader'
import FallbackImage from '~/assets/images/fallback_image.svg?component'
import twitterIcon from '~/assets/icons/twitter.svg?component'
import facebookIcon from '~/assets/icons/facebook.svg?component'

const config = useRuntimeConfig()

const nuxtApp = useNuxtApp()
const route = useRoute()

const indexStore = useIndexStore()

const { data: person, status } = useAsyncData(async () => {
  if (typeof route.params.writer !== 'string') {
    return null
  }

  return await nuxtApp.$api.content.writerFull(route.params.writer)
})

const personImage = computed(() => {
  return person.value?.HeadImage ?? ''
})

const { data: articles } = await useAsyncData(async () => {
  return await nuxtApp.$api.content.articles({
    type: 'writer',
    id: route.params?.writer.toString(),
    paper: indexStore.currentPaper?.RecordId ?? 1,
  })
})
</script>
